import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { useAppDispatch } from '@/hooks/redux'
import { useRunReportGuard } from '@/pages/RunReport/guards/useRunReportGuard'
import {
  ERefineFilter,
  EReportType,
  ERunReportQueryParam,
  ERunReportStep,
} from '@/pages/RunReport/types'
import { useFindSubjectMutation } from '@/store/api/RunReport/RunReport.query'
import {
  clearSocialMedia,
  setIsPrefillForm,
} from '@/store/api/RunReport/RunReport.slice'
import { filterEmptyFields } from '@/utils/filterEmptyFields'

import { FilterChip } from '../../components/FilterChip'
import { RefineSearchDialog } from '../../components/RefineSearchDialog'
import { SearchResultRow } from '../../components/SearchResultRow'
import { SearchResultsPagination } from '../../components/SearchResultsPagination'
import { SearchResultsTopBar } from '../../components/SearchResultsTopBar'

const PAGE_SIZE = 10
const FIND_REPORT_TYPE = EReportType.AiReport

const parseSearchQuery = (
  raw: string,
): { linkedinUrl: string; firstName: string; lastName: string } => {
  const trimmed = raw.trim()
  if (trimmed.includes('linkedin.com')) {
    return { linkedinUrl: trimmed, firstName: '', lastName: '' }
  }
  const parts = trimmed.split(/\s+/)
  return {
    linkedinUrl: '',
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  }
}

export const SearchResults = () => {
  useRunReportGuard(ERunReportStep.SearchResults)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const q = searchParams.get(ERunReportQueryParam.Q) ?? ''
  const useCaseId = searchParams.get(ERunReportQueryParam.UseCaseId) ?? ''
  const employer = searchParams.get(ERefineFilter.Employer) ?? ''
  const school = searchParams.get(ERefineFilter.School) ?? ''
  const page = Number(searchParams.get(ERunReportQueryParam.Page) ?? '1') || 1

  const [openFilter, setOpenFilter] = useState<ERefineFilter | null>(null)

  const [findSubject, { data, isLoading, isError }] = useFindSubjectMutation()

  useEffect(() => {
    if (!q || !useCaseId) return
    const { linkedinUrl, firstName, lastName } = parseSearchQuery(q)
    const request = {
      page,
      linkedinUrl,
      firstName,
      lastName,
      company: employer,
      school,
      reportType: FIND_REPORT_TYPE,
    }
    findSubject(filterEmptyFields(request))
  }, [q, useCaseId, employer, school, page, findSubject])

  const updateParams = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        mutator(next)
        return next
      })
    },
    [setSearchParams],
  )

  const handleSubjectSearch = (value: string) => {
    updateParams((p) => {
      p.set(ERunReportQueryParam.Q, value)
      p.set(ERunReportQueryParam.Page, '1')
    })
  }

  const handleRefineApply = (filter: ERefineFilter) => (value: string) => {
    updateParams((p) => {
      if (value) p.set(filter, value)
      else p.delete(filter)
      p.set(ERunReportQueryParam.Page, '1')
    })
  }

  const handlePageChange = (nextPage: number) => {
    updateParams((p) => p.set(ERunReportQueryParam.Page, String(nextPage)))
  }

  const goToConfirmSubjectDetails = () => {
    dispatch(clearSocialMedia())
    dispatch(setIsPrefillForm(false))
    navigate(AppRoutes.runReportConfirmSubjectDetails)
  }

  const items = data?.info.items ?? []
  const totalCount = data?.info.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const showEmpty = !isLoading && !isError && items.length === 0

  return (
    <WeScreen
      isLoading={isLoading}
      isError={isError}
      useNextButton={false}
      useSkipButton={false}
      onClickBack={() => navigate(AppRoutes.runReportSearchForm)}
      hideHeaderOnMobile
    >
      <div className="mx-auto flex h-full min-h-0 w-full max-w-[1200px] flex-col gap-3 p-3 md:gap-4 md:px-6 md:py-6">
        <SearchResultsTopBar
          value={q}
          onSubmit={handleSubjectSearch}
          onCantSpot={goToConfirmSubjectDetails}
        />

        <div className="flex shrink-0 items-center gap-3">
          <FilterChip
            label={t('dashboard.runReport.searchResults.employer')}
            value={employer}
            onClick={() => setOpenFilter(ERefineFilter.Employer)}
          />
          <FilterChip
            label={t('dashboard.runReport.searchResults.school')}
            value={school}
            onClick={() => setOpenFilter(ERefineFilter.School)}
          />
        </div>

        {showEmpty ? (
          <EmptyState onTryAdvancedSearch={goToConfirmSubjectDetails} />
        ) : (
          <>
            <p className="shrink-0 text-base font-medium text-neutralColor max-md:hidden">
              {t('dashboard.runReport.searchResults.results')}
            </p>
            <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[repeat(10,minmax(0,1fr))] overflow-y-auto">
              {items.map((item, index) => (
                <SearchResultRow
                  key={`${item.profileURL}-${index}`}
                  data={item}
                />
              ))}
            </div>

            <p className="shrink-0 text-center text-[10px] leading-tight text-neutralColor60 md:text-sm md:leading-normal">
              {t('dashboard.runReport.searchResults.legalDisclaimer')}
            </p>

            <SearchResultsPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>

      <RefineSearchDialog
        open={openFilter === ERefineFilter.Employer}
        onOpenChange={(o) => !o && setOpenFilter(null)}
        title={t('dashboard.runReport.searchResults.employer')}
        description={t('dashboard.runReport.searchResults.employerDescription')}
        placeholder={t('dashboard.runReport.searchResults.employerPlaceholder')}
        value={employer}
        onSubmit={handleRefineApply(ERefineFilter.Employer)}
      />
      <RefineSearchDialog
        open={openFilter === ERefineFilter.School}
        onOpenChange={(o) => !o && setOpenFilter(null)}
        title={t('dashboard.runReport.searchResults.school')}
        description={t('dashboard.runReport.searchResults.schoolDescription')}
        placeholder={t('dashboard.runReport.searchResults.schoolPlaceholder')}
        value={school}
        onSubmit={handleRefineApply(ERefineFilter.School)}
      />
    </WeScreen>
  )
}

const EmptyState = ({
  onTryAdvancedSearch,
}: {
  onTryAdvancedSearch: () => void
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24 text-center">
      <h2 className="text-2xl font-medium text-neutralColor">
        {t('dashboard.runReport.searchResults.emptyTitle')}
      </h2>
      <p className="max-w-md text-sm text-neutralColor60">
        {t('dashboard.runReport.searchResults.emptyDescription')}
      </p>
      <Button variant="secondary" onClick={onTryAdvancedSearch}>
        {t('dashboard.runReport.searchResults.tryAdvancedSearch')}
      </Button>
    </div>
  )
}
